import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeEuro, BookOpenText, FileCheck2, Stethoscope, Upload, X } from "lucide-react";
import { IMAGES, TESSERE_NOTE } from "../../data/content";
import { inputCls, labelCls } from "./ContactForm";
import { TessereCatalog } from "./TessereCatalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nome: "", cognome: "", indirizzo: "", email: "", cellulare: "", tipo_tessera: "partecipante" };

export const MembershipForm = () => {
  const [tessere, setTessere] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [documento, setDocumento] = useState(null);
  const [certificato, setCertificato] = useState(null);
  const [sending, setSending] = useState(false);
  const [catalog, setCatalog] = useState({ open: false, tab: "prezzi" });

  useEffect(() => {
    axios.get(`${API}/tessere`).then((r) => setTessere(r.data)).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!documento) {
      toast.error("Il documento d'identità è obbligatorio.");
      return;
    }
    if (needsCertificato && !certificato) {
      toast.error("Per le licenze Promo e Sportiva è obbligatorio il certificato medico sportivo.");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("origin_url", window.location.origin);
      data.append("documento", documento);
      if (needsCertificato && certificato) data.append("certificato", certificato);
      const r = await axios.post(`${API}/tesseramenti`, data);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito. Controlla i dati e riprova.");
      setSending(false);
    }
  };

  const selected = tessere.find((t) => t.id === form.tipo_tessera);
  const legendTessere = tessere.filter((t) => t.gruppo === "legend");
  const mspSelected = selected && selected.gruppo !== "legend" ? selected : null;
  const openCatalog = (tab) => setCatalog({ open: true, tab });
  const isRinnovo = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("rinnovo") === "1";
  const needsCertificato = Boolean(selected?.certificato_richiesto);

  return (
    <form
      data-testid="membership-form"
      onSubmit={onSubmit}
      className="space-y-4 bg-neutral-950 p-8 border border-neutral-800 border-t-4 border-t-red-600 h-full flex flex-col"
    >
      <div>
        <div className="mono-label text-xs text-red-500 uppercase tracking-wider mb-1">02 &bull; Tesseramento Club &bull; affiliazione CONI</div>
        <h3 className="font-display text-2xl font-black uppercase text-white">Tessera Legend Team</h3>
        <p className="text-xs text-neutral-400 mt-1">
          Include i servizi del Legend Village, officina, pub, raduni ed eventi, o la partecipazione come pilota/navigatore.
        </p>
      </div>

      {isRinnovo && (
        <div data-testid="rinnovo-banner" className="border border-red-600 bg-red-950/30 p-3 text-xs text-neutral-200">
          <span className="mono-label text-[10px] uppercase tracking-wider text-red-500 block">Rinnovo tessera</span>
          Bentornato! Scegli la stessa tessera (o una nuova categoria), ricarica il documento d'identità e completa il pagamento: la tessera resta attiva senza interruzioni.
        </div>
      )}

      <fieldset>
        <legend className={labelCls}>Tipo di tessera</legend>
        <div data-testid="tessera-options" className="grid grid-cols-2 gap-2">
          {legendTessere.map((t) => (
            <label
              key={t.id}
              data-testid={`tessera-option-${t.id}`}
              className={`relative cursor-pointer border p-3 transition-colors ${
                form.tipo_tessera === t.id ? "border-red-600 bg-red-950/30" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
              }`}
            >
              <input type="radio" name="tipo_tessera" value={t.id} checked={form.tipo_tessera === t.id} onChange={onChange} className="sr-only" />
              <span className="block text-sm font-black uppercase text-white">{t.label}</span>
              <span className="mono-label text-xs text-red-500 font-bold">{t.prezzo.toFixed(0)} EURO</span>
              {t.promo && (
                <span className="absolute top-2 right-2 mono-label text-[9px] uppercase bg-red-600 text-white px-1.5 py-0.5">In promozione</span>
              )}
            </label>
          ))}
        </div>
        <p data-testid="tessera-note" className="text-[11px] text-neutral-400 mt-2 leading-relaxed">{TESSERE_NOTE}</p>

        {mspSelected && (
          <div data-testid="msp-selected" className="mt-3 flex items-center justify-between gap-3 border border-red-600 bg-red-950/30 p-3">
            <div className="min-w-0">
              <div className="mono-label text-[10px] text-red-500 uppercase tracking-wider">Licenza MSP 2026 selezionata</div>
              <div className="text-sm font-black uppercase text-white truncate">{mspSelected.label} — {mspSelected.prezzo.toFixed(0)} €</div>
              <div className="text-[11px] text-neutral-400 truncate">{mspSelected.disciplina}</div>
            </div>
            <button
              type="button"
              data-testid="msp-selected-remove"
              onClick={() => setForm({ ...form, tipo_tessera: "partecipante" })}
              aria-label="Rimuovi licenza MSP selezionata"
              className="shrink-0 p-2 text-neutral-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <button
            type="button"
            data-testid="open-tessere-prezzi"
            onClick={() => openCatalog("prezzi")}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-neutral-700 hover:border-red-600 text-neutral-200 hover:text-red-500 mono-label text-[11px] font-bold uppercase tracking-wider transition-colors"
          >
            <BadgeEuro className="w-4 h-4" /> Tutte le categorie e prezzi MSP 2026
          </button>
          <button
            type="button"
            data-testid="open-tessere-spiegazioni"
            onClick={() => openCatalog("spiegazioni")}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-neutral-100 hover:bg-red-600 text-black hover:text-white mono-label text-[11px] font-black uppercase tracking-wider transition-colors"
          >
            <BookOpenText className="w-4 h-4" /> Spiegami le categorie
          </button>
        </div>
      </fieldset>

      <TessereCatalog
        open={catalog.open}
        tab={catalog.tab}
        onOpenChange={(open) => setCatalog((c) => ({ ...c, open }))}
        tessere={tessere}
        selected={form.tipo_tessera}
        onSelect={(id) => setForm({ ...form, tipo_tessera: id })}
      />

      <div data-testid="tessera-preview" className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_200px] gap-4 items-center border border-neutral-800 bg-neutral-900 p-3">
        <div className="min-w-0">
          <div className="mono-label text-[10px] text-red-500 uppercase tracking-wider">La tua tessera</div>
          <p className="text-xs text-neutral-300 leading-relaxed mt-1">
            Riceverai la <span className="text-white font-bold">tessera Legend Member Gold</span> e la
            <span className="text-white font-bold"> tessera CONI</span>: la registrazione ufficiale avviene
            entro 5 giorni con codice identificativo.
          </p>
        </div>
        <img
          src={IMAGES.tesseraMemberGold}
          alt="Tessera Legend Member Gold"
          className="w-28 sm:w-[200px] border border-neutral-700"
          loading="lazy"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="t-nome" className={labelCls}>Nome *</label>
          <input id="t-nome" name="nome" data-testid="membership-nome-input" className={inputCls} value={form.nome} onChange={onChange} required minLength={2} />
        </div>
        <div>
          <label htmlFor="t-cognome" className={labelCls}>Cognome *</label>
          <input id="t-cognome" name="cognome" data-testid="membership-cognome-input" className={inputCls} value={form.cognome} onChange={onChange} required minLength={2} />
        </div>
      </div>
      <div>
        <label htmlFor="t-indirizzo" className={labelCls}>Indirizzo di residenza *</label>
        <input id="t-indirizzo" name="indirizzo" data-testid="membership-indirizzo-input" className={inputCls} placeholder="Via, numero, CAP, città" value={form.indirizzo} onChange={onChange} required minLength={5} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="t-email" className={labelCls}>Email *</label>
          <input id="t-email" name="email" type="email" data-testid="membership-email-input" className={inputCls} value={form.email} onChange={onChange} required />
        </div>
        <div>
          <label htmlFor="t-cellulare" className={labelCls}>Numero cellulare *</label>
          <input id="t-cellulare" name="cellulare" type="tel" data-testid="membership-cellulare-input" className={inputCls} placeholder="+39 333 1234567" pattern="^\+?[0-9 ]{8,16}$" value={form.cellulare} onChange={onChange} required />
        </div>
      </div>

      <div>
        <label htmlFor="t-documento" className={labelCls}>Documento d'identità * (JPG, PNG, PDF — max 10 MB)</label>
        <label
          htmlFor="t-documento"
          data-testid="membership-documento-dropzone"
          className={`flex items-center gap-3 cursor-pointer border border-dashed p-4 text-xs ${
            documento ? "border-red-600 text-white" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"
          }`}
        >
          {documento ? <FileCheck2 className="w-5 h-5 text-red-500 shrink-0" /> : <Upload className="w-5 h-5 shrink-0" />}
          <span className="truncate">{documento ? documento.name : "Carica carta d'identità, patente o passaporto"}</span>
        </label>
        <input
          id="t-documento"
          type="file"
          data-testid="membership-documento-input"
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
          className="sr-only"
          onChange={(e) => setDocumento(e.target.files?.[0] || null)}
          required
        />
      </div>

      {needsCertificato && (
        <div data-testid="certificato-block" className="border border-red-600/60 bg-red-950/20 p-3">
          <label htmlFor="t-certificato" className={labelCls}>
            Certificato medico sportivo * ({selected.gruppo.includes("sportiva") ? "agonistico" : "non agonistico"} — JPG, PNG, PDF)
          </label>
          <label
            htmlFor="t-certificato"
            data-testid="membership-certificato-dropzone"
            className={`flex items-center gap-3 cursor-pointer border border-dashed p-4 text-xs ${
              certificato ? "border-red-600 text-white" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"
            }`}
          >
            {certificato ? <FileCheck2 className="w-5 h-5 text-red-500 shrink-0" /> : <Stethoscope className="w-5 h-5 shrink-0" />}
            <span className="truncate">{certificato ? certificato.name : "Carica il certificato medico richiesto per la licenza scelta"}</span>
          </label>
          <input
            id="t-certificato"
            type="file"
            data-testid="membership-certificato-input"
            accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
            className="sr-only"
            onChange={(e) => setCertificato(e.target.files?.[0] || null)}
            required
          />
          <p className="text-[11px] text-neutral-400 mt-2">
            Licenze Promo: visita medica generica. Licenze Sportiva: visita medico-sportiva agonistica (A1 auto / B1 moto).
          </p>
        </div>
      )}

      <div className="pt-2 mt-auto flex flex-col sm:flex-row justify-between items-center gap-4">
        <span className="mono-label text-xs text-neutral-400">Pagamento sicuro online con carta &bull; Stripe</span>
        <button
          type="submit"
          data-testid="membership-submit-button"
          disabled={sending}
          className="w-full sm:w-auto px-8 py-3 bg-white hover:bg-red-600 hover:text-white disabled:opacity-50 text-black font-black uppercase text-xs tracking-widest transition-colors"
        >
          {sending ? "Reindirizzamento..." : `Tesserati — ${selected ? `${selected.prezzo.toFixed(0)} €` : ""}`}
        </button>
      </div>
    </form>
  );
};
